import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  dark: {
    key: 'dark',
    label: 'Dark',
    bg: '#0a0a0f',
    bgSecondary: '#12121a',
    bgCard: '#1a1a24',
    border: '#1e1e2e',
    text: '#f0f0f3',
    textSecondary: '#9e9ea8',
    textMuted: '#6b6b78',
    accent: '#7c3aed',
    accentHover: '#6d28d9',
    shadow: 'none',
    cardRadius: '0.75rem',
  },
  light: {
    key: 'light',
    label: 'Light',
    bg: '#f5f5f5',
    bgSecondary: '#ffffff',
    bgCard: '#ffffff',
    border: '#e5e5e5',
    text: '#111111',
    textSecondary: '#555555',
    textMuted: '#999999',
    accent: '#7c3aed',
    accentHover: '#6d28d9',
    shadow: '0 1px 3px rgba(0,0,0,0.06)',
    cardRadius: '0.75rem',
  },
  neumorphic: {
    key: 'neumorphic',
    label: 'Matte',
    bg: '#2a2a32',
    bgSecondary: '#303038',
    bgCard: '#33333c',
    border: 'rgba(255,255,255,0.04)',
    text: '#e8e8ec',
    textSecondary: '#a0a0a8',
    textMuted: '#707078',
    accent: '#c084fc',
    accentHover: '#a855f7',
    shadow: '6px 6px 14px rgba(0,0,0,0.35), -4px -4px 10px rgba(255,255,255,0.03)',
    shadowInset: 'inset 3px 3px 6px rgba(0,0,0,0.25), inset -2px -2px 5px rgba(255,255,255,0.03)',
    cardRadius: '1rem',
  },
};

const THEME_ORDER = ['dark', 'light'];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('fx_theme');
    if (saved === 'neumorphic') return 'dark';
    if (saved && THEMES[saved]) return saved;
    return 'dark';
  });

  useEffect(() => {
    localStorage.setItem('fx_theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    
    // Set CSS variables from theme
    const t = THEMES[theme];
    const root = document.documentElement;
    root.style.setProperty('--th-bg', t.bg);
    root.style.setProperty('--th-bg2', t.bgSecondary);
    root.style.setProperty('--th-card', t.bgCard);
    root.style.setProperty('--th-border', t.border);
    root.style.setProperty('--th-text', t.text);
    root.style.setProperty('--th-text2', t.textSecondary);
    root.style.setProperty('--th-muted', t.textMuted);
    root.style.setProperty('--th-accent', t.accent);
    root.style.setProperty('--th-shadow', t.shadow);
    root.style.setProperty('--th-radius', t.cardRadius);

    // Class for tailwind selectors
    root.classList.remove('dark', 'light', 'neumorphic');
    root.classList.add(theme);
  }, [theme]);

  const cycleTheme = () => {
    const idx = THEME_ORDER.indexOf(theme);
    setTheme(THEME_ORDER[(idx + 1) % THEME_ORDER.length]);
  };

  const value = {
    theme,
    setTheme,
    toggleTheme: cycleTheme,
    cycleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isNeumorphic: theme === 'neumorphic',
    isDarkish: theme === 'dark' || theme === 'neumorphic',
    T: THEMES[theme],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Keep backwards compat
export const themeColors = {
  dark: {
    bg: 'bg-[#0a0a0f]',
    bgSecondary: 'bg-[#12121a]',
    bgCard: 'bg-[#1a1a24]',
    border: 'border-gray-800',
    borderHover: 'hover:border-gray-700',
    text: 'text-white',
    textSecondary: 'text-gray-400',
    textMuted: 'text-gray-500',
  },
  light: {
    bg: 'bg-gray-50',
    bgSecondary: 'bg-white',
    bgCard: 'bg-white',
    border: 'border-gray-200',
    borderHover: 'hover:border-gray-300',
    text: 'text-gray-900',
    textSecondary: 'text-gray-600',
    textMuted: 'text-gray-500',
  }
};

export default ThemeContext;
